/**
 * Mock EWAS Data for Development
 * Based on NRC EWAS Sudan data pipeline structure
 * https://nrc-ewas.masae-analytics.com/
 */

import type {
  EWASDataSource,
  EWASStatistics,
  EWASQueryFilters,
  ACLEDEvent,
  IOMDTMRecord,
  FEWSNETRecord,
  DataminrAlert,
  IDMCRecord,
  GloFASRecord,
  AnyEWASRecord,
} from '../services/nrc-ewas/types';

// Mock EWAS Data Sources (matching real EWAS pipeline)
export const mockEWASSources: EWASDataSource[] = [
  {
    id: 'acled',
    name: 'Armed Conflict Location & Event Data',
    shortName: 'ACLED',
    type: 'API',
    status: 'online',
    recordCount: 22458,
    lastUpdated: new Date(Date.now() - 15 * 60 * 1000).toISOString(), // 15 min ago
    description: 'Real-time conflict event monitoring - battles, violence against civilians, protests',
    category: 'conflict',
    apiEndpoint: 'https://api.acleddata.com',
  },
  {
    id: 'acled_cast',
    name: 'ACLED Conflict Analysis System Tracker',
    shortName: 'ACLED CAST',
    type: 'API',
    status: 'online',
    recordCount: 228,
    lastUpdated: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2h ago
    description: 'Forecasted conflict events based on ACLED predictive models',
    category: 'conflict',
    apiEndpoint: 'https://api.acleddata.com/forecast',
  },
  {
    id: 'iom_dtm',
    name: 'IOM Displacement Tracking Matrix',
    shortName: 'IOM DTM',
    type: 'API',
    status: 'online',
    recordCount: 31302,
    lastUpdated: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    description: 'Displacement tracking including IDPs, returnees, and cross-border movements',
    category: 'displacement',
    apiEndpoint: 'https://dtm.iom.int/api',
  },
  {
    id: 'idmc_gidd',
    name: 'IDMC Global Internal Displacement Database',
    shortName: 'IDMC-GIDD',
    type: 'API',
    status: 'online',
    recordCount: 215,
    lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    description: 'Annual displacement stock figures by country and cause',
    category: 'displacement',
    apiEndpoint: 'https://api.idmcdb.org',
  },
  {
    id: 'idmc_idu',
    name: 'IDMC Internal Displacement Updates',
    shortName: 'IDMC-IDU',
    type: 'API',
    status: 'online',
    recordCount: 203,
    lastUpdated: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    description: 'Event-based displacement updates as they occur',
    category: 'displacement',
    apiEndpoint: 'https://api.idmcdb.org/idu',
  },
  {
    id: 'fewsnet',
    name: 'Famine Early Warning Systems Network',
    shortName: 'FEWSNET',
    type: 'API',
    status: 'online',
    recordCount: 208,
    lastUpdated: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    description: 'Food security and IPC phase classification data',
    category: 'food_security',
    apiEndpoint: 'https://fews.net/api',
  },
  {
    id: 'glofas',
    name: 'Global Flood Awareness System',
    shortName: 'GloFAS',
    type: 'API',
    status: 'online',
    recordCount: 3,
    lastUpdated: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    description: 'River flood forecasting and early warning',
    category: 'flood',
    apiEndpoint: 'https://cds.climate.copernicus.eu/glofas',
  },
  {
    id: 'dataminr',
    name: 'Dataminr Real-Time Alerts',
    shortName: 'Dataminr',
    type: 'API',
    status: 'online',
    recordCount: 725,
    lastUpdated: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // 5 min ago
    description: 'AI-powered real-time crisis alerts from social media and news',
    category: 'social',
    apiEndpoint: 'https://api.dataminr.com',
  },
  {
    id: 'dtm_flash',
    name: 'IOM DTM Flash Alerts',
    shortName: 'DTM Flash',
    type: 'RSS',
    status: 'online',
    recordCount: 77,
    lastUpdated: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
    description: 'Rapid displacement alerts for sudden onset events',
    category: 'displacement',
  },
  {
    id: 'reliefweb',
    name: 'ReliefWeb Humanitarian Reports',
    shortName: 'ReliefWeb',
    type: 'API',
    status: 'online',
    recordCount: 7,
    lastUpdated: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    description: 'Humanitarian situation reports and analysis',
    category: 'reports',
    apiEndpoint: 'https://api.reliefweb.int',
  },
];

// Mock EWAS Statistics
export const mockEWASStatistics: EWASStatistics = {
  totalRecords: 55426,
  sourcesOnline: 10,
  sourcesTotal: 10,
  lastSync: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
  recordsBySource: {
    acled: 22458,
    acled_cast: 228,
    iom_dtm: 31302,
    idmc_gidd: 215,
    idmc_idu: 203,
    fewsnet: 208,
    glofas: 3,
    dataminr: 725,
    dtm_flash: 77,
    reliefweb: 7,
  },
  alertsToday: 47,
  criticalAlerts: 12,
};

// Sudan-specific ACLED events
const mockACLEDEvents: ACLEDEvent[] = [
  {
    id: 'acled-001',
    source: 'acled',
    sourceName: 'ACLED',
    variable: 'conflict_event',
    value: 1,
    location: { name: 'Khartoum', lat: 15.50, lng: 32.53, adminLevel: 1, admin1: 'Khartoum' },
    date: '2026-01-31',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    severity: 'critical',
    description: 'Heavy clashes between SAF and RSF forces in central Khartoum, multiple casualties reported',
    eventType: 'battles',
    subEventType: 'armed_clash',
    fatalities: 23,
    actor1: 'Sudanese Armed Forces',
    actor2: 'Rapid Support Forces',
    notes: 'Urban warfare continues with heavy weapons use',
  },
  {
    id: 'acled-002',
    source: 'acled',
    sourceName: 'ACLED',
    variable: 'conflict_event',
    value: 1,
    location: { name: 'El Fasher', lat: 13.63, lng: 25.35, adminLevel: 1, admin1: 'North Darfur' },
    date: '2026-01-31',
    timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    severity: 'critical',
    description: 'RSF assault on El Fasher city center, civilian areas affected',
    eventType: 'battles',
    subEventType: 'government_regains_territory',
    fatalities: 45,
    actor1: 'Rapid Support Forces',
    actor2: 'Joint Protection Forces',
    notes: 'Fighting near IDP camps',
  },
  {
    id: 'acled-003',
    source: 'acled',
    sourceName: 'ACLED',
    variable: 'conflict_event',
    value: 1,
    location: { name: 'Nyala', lat: 12.05, lng: 24.88, adminLevel: 1, admin1: 'South Darfur' },
    date: '2026-01-30',
    timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
    severity: 'high',
    description: 'Armed clashes in Nyala market area, several injured',
    eventType: 'battles',
    subEventType: 'armed_clash',
    fatalities: 8,
    actor1: 'Rapid Support Forces',
    actor2: 'Sudanese Armed Forces',
  },
  {
    id: 'acled-004',
    source: 'acled',
    sourceName: 'ACLED',
    variable: 'conflict_event',
    value: 1,
    location: { name: 'Zalingei', lat: 12.91, lng: 23.47, adminLevel: 1, admin1: 'Central Darfur' },
    date: '2026-01-30',
    timestamp: new Date(Date.now() - 20 * 60 * 60 * 1000).toISOString(),
    severity: 'high',
    description: 'Violence against civilians reported in Zalingei town',
    eventType: 'violence_against_civilians',
    subEventType: 'attack',
    fatalities: 12,
    actor1: 'Unidentified Armed Group',
  },
  {
    id: 'acled-005',
    source: 'acled',
    sourceName: 'ACLED',
    variable: 'conflict_event',
    value: 1,
    location: { name: 'Kassala', lat: 15.45, lng: 36.40, adminLevel: 1, admin1: 'Kassala' },
    date: '2026-01-29',
    timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
    severity: 'medium',
    description: 'Protest against price increases dispersed with tear gas',
    eventType: 'protests',
    subEventType: 'protest_with_intervention',
    fatalities: 0,
    actor1: 'Protesters',
    actor2: 'Police Forces',
  },
  {
    id: 'acled-cast-001',
    source: 'acled_cast',
    sourceName: 'ACLED CAST',
    variable: 'forecast_event',
    value: 1,
    location: { name: 'Omdurman', lat: 15.65, lng: 32.48, adminLevel: 1, admin1: 'Khartoum' },
    date: '2026-02-03',
    timestamp: new Date().toISOString(),
    severity: 'high',
    description: 'Forecast: High probability of armed clashes in Omdurman within 72 hours',
    eventType: 'battles',
    fatalities: 0,
    isForecast: true,
  },
];

// Sudan-specific IOM DTM displacement data
const mockIOMDTMRecords: IOMDTMRecord[] = [
  {
    id: 'dtm-001',
    source: 'iom_dtm',
    sourceName: 'IOM DTM',
    variable: 'displacement',
    value: 85000,
    unit: 'individuals',
    location: { name: 'Zalingei', lat: 12.91, lng: 23.47, adminLevel: 1, admin1: 'Central Darfur' },
    date: '2026-01-30',
    timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    severity: 'critical',
    description: 'Large-scale displacement from Zalingei due to ongoing conflict',
    displacementType: 'idp',
    individuals: 85000,
    households: 14200,
    reason: 'Armed conflict',
  },
  {
    id: 'dtm-002',
    source: 'iom_dtm',
    sourceName: 'IOM DTM',
    variable: 'displacement',
    value: 42000,
    unit: 'individuals',
    location: { name: 'El Geneina', lat: 13.45, lng: 22.45, adminLevel: 1, admin1: 'West Darfur' },
    date: '2026-01-29',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    severity: 'critical',
    description: 'Cross-border displacement to Chad from West Darfur',
    displacementType: 'cross_border',
    individuals: 42000,
    households: 7500,
    destination: { name: 'Adré, Chad', lat: 13.47, lng: 22.19, adminLevel: 0 },
    reason: 'Armed conflict and violence',
  },
  {
    id: 'dtm-003',
    source: 'iom_dtm',
    sourceName: 'IOM DTM',
    variable: 'displacement',
    value: 28000,
    unit: 'individuals',
    location: { name: 'Kassala', lat: 15.45, lng: 36.40, adminLevel: 1, admin1: 'Kassala' },
    date: '2026-01-28',
    timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    severity: 'high',
    description: 'Refugee influx from Eritrea continues',
    displacementType: 'cross_border',
    individuals: 28000,
    households: 4800,
    origin: { name: 'Eritrea', lat: 15.18, lng: 39.78, adminLevel: 0 },
    reason: 'Economic and political factors',
  },
  {
    id: 'dtm-004',
    source: 'iom_dtm',
    sourceName: 'IOM DTM',
    variable: 'displacement',
    value: 15000,
    unit: 'individuals',
    location: { name: 'Gedaref', lat: 14.03, lng: 35.40, adminLevel: 1, admin1: 'Gedaref' },
    date: '2026-01-27',
    timestamp: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString(),
    severity: 'high',
    description: 'Secondary displacement from Khartoum arrivals',
    displacementType: 'idp',
    individuals: 15000,
    households: 2800,
    origin: { name: 'Khartoum', lat: 15.50, lng: 32.53, adminLevel: 1 },
    reason: 'Fleeing conflict',
  },
  {
    id: 'dtm-flash-001',
    source: 'dtm_flash',
    sourceName: 'DTM Flash',
    variable: 'flash_displacement',
    value: 12000,
    unit: 'individuals',
    location: { name: 'Port Sudan', lat: 19.62, lng: 37.22, adminLevel: 1, admin1: 'Red Sea' },
    date: '2026-01-31',
    timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    severity: 'high',
    description: 'Rapid influx of IDPs arriving in Port Sudan from conflict zones',
    displacementType: 'idp',
    individuals: 12000,
    households: 2200,
    reason: 'Sudden onset displacement',
  },
];

// FEWSNET food security data
const mockFEWSNETRecords: FEWSNETRecord[] = [
  {
    id: 'fews-001',
    source: 'fewsnet',
    sourceName: 'FEWSNET',
    variable: 'ipc_phase',
    value: 5,
    location: { name: 'West Darfur', lat: 13.45, lng: 22.45, adminLevel: 1, admin1: 'West Darfur' },
    date: '2026-01-15',
    timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    severity: 'critical',
    description: 'Famine conditions (IPC Phase 5) confirmed in West Darfur',
    ipcPhase: 5,
    populationInPhase: 890000,
    projectionPeriod: 'Feb-May 2026',
    analysisType: 'projected',
  },
  {
    id: 'fews-002',
    source: 'fewsnet',
    sourceName: 'FEWSNET',
    variable: 'ipc_phase',
    value: 4,
    location: { name: 'South Kordofan', lat: 11.20, lng: 29.42, adminLevel: 1, admin1: 'South Kordofan' },
    date: '2026-01-15',
    timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    severity: 'critical',
    description: 'Emergency (IPC Phase 4) food insecurity in South Kordofan',
    ipcPhase: 4,
    populationInPhase: 650000,
    projectionPeriod: 'Feb-May 2026',
    analysisType: 'current',
  },
  {
    id: 'fews-003',
    source: 'fewsnet',
    sourceName: 'FEWSNET',
    variable: 'ipc_phase',
    value: 4,
    location: { name: 'Blue Nile', lat: 11.77, lng: 34.35, adminLevel: 1, admin1: 'Blue Nile' },
    date: '2026-01-15',
    timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    severity: 'high',
    description: 'Emergency food insecurity affecting Blue Nile state',
    ipcPhase: 4,
    populationInPhase: 280000,
    projectionPeriod: 'Feb-May 2026',
    analysisType: 'current',
  },
  {
    id: 'fews-004',
    source: 'fewsnet',
    sourceName: 'FEWSNET',
    variable: 'ipc_phase',
    value: 3,
    location: { name: 'Kassala', lat: 15.45, lng: 36.40, adminLevel: 1, admin1: 'Kassala' },
    date: '2026-01-15',
    timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    severity: 'medium',
    description: 'Crisis (IPC Phase 3) food insecurity in Kassala',
    ipcPhase: 3,
    populationInPhase: 420000,
    projectionPeriod: 'Feb-May 2026',
    analysisType: 'current',
  },
];

// Dataminr social media alerts
const mockDataminrAlerts: DataminrAlert[] = [
  {
    id: 'dm-001',
    source: 'dataminr',
    sourceName: 'Dataminr',
    variable: 'social_alert',
    value: 1,
    location: { name: 'Khartoum', lat: 15.50, lng: 32.53, adminLevel: 1, admin1: 'Khartoum' },
    date: '2026-01-31',
    timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    severity: 'critical',
    description: 'Multiple reports of heavy shelling in Khartoum North residential areas',
    alertType: 'Armed Conflict',
    urgency: 'breaking',
    confidence: 0.94,
    keywords: ['shelling', 'Khartoum', 'residential', 'casualties'],
  },
  {
    id: 'dm-002',
    source: 'dataminr',
    sourceName: 'Dataminr',
    variable: 'social_alert',
    value: 1,
    location: { name: 'Port Sudan', lat: 19.62, lng: 37.22, adminLevel: 1, admin1: 'Red Sea' },
    date: '2026-01-31',
    timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    severity: 'high',
    description: 'Cholera cases reported spreading in Port Sudan IDP settlements',
    alertType: 'Disease Outbreak',
    urgency: 'urgent',
    confidence: 0.88,
    keywords: ['cholera', 'Port Sudan', 'IDP', 'outbreak'],
  },
  {
    id: 'dm-003',
    source: 'dataminr',
    sourceName: 'Dataminr',
    variable: 'social_alert',
    value: 1,
    location: { name: 'El Fasher', lat: 13.63, lng: 25.35, adminLevel: 1, admin1: 'North Darfur' },
    date: '2026-01-31',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    severity: 'high',
    description: 'Reports of humanitarian convoy attacked near El Fasher',
    alertType: 'Humanitarian Access',
    urgency: 'urgent',
    confidence: 0.82,
    keywords: ['convoy', 'attack', 'humanitarian', 'El Fasher'],
  },
  {
    id: 'dm-004',
    source: 'dataminr',
    sourceName: 'Dataminr',
    variable: 'social_alert',
    value: 1,
    location: { name: 'Nyala', lat: 12.05, lng: 24.88, adminLevel: 1, admin1: 'South Darfur' },
    date: '2026-01-30',
    timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    severity: 'medium',
    description: 'Market closure reported in Nyala due to security concerns',
    alertType: 'Civil Unrest',
    urgency: 'important',
    confidence: 0.78,
    keywords: ['market', 'closure', 'security', 'Nyala'],
  },
];

// GloFAS flood alerts
const mockGloFASRecords: GloFASRecord[] = [
  {
    id: 'glofas-001',
    source: 'glofas',
    sourceName: 'GloFAS',
    variable: 'flood_alert',
    value: 1,
    location: { name: 'White Nile', lat: 13.16, lng: 32.50, adminLevel: 1, admin1: 'White Nile' },
    date: '2026-01-31',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    severity: 'medium',
    description: 'Elevated flood risk for White Nile state - seasonal monitoring',
    alertLevel: 'yellow',
    returnPeriod: '5yr',
    riverName: 'White Nile',
    basinName: 'Nile Basin',
    leadTime: '5-day forecast',
  },
];

// IDMC displacement records
const mockIDMCRecords: IDMCRecord[] = [
  {
    id: 'idmc-001',
    source: 'idmc_gidd',
    sourceName: 'IDMC-GIDD',
    variable: 'internal_displacement',
    value: 10800000,
    unit: 'individuals',
    location: { name: 'Sudan', lat: 15.5, lng: 30.0, adminLevel: 0 },
    date: '2025-12-31',
    timestamp: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    severity: 'critical',
    description: 'Total internal displacement stock figure for Sudan',
    displacementType: 'conflict',
    stockFigure: 10800000,
    year: 2025,
  },
];

// Combined mock records getter
export function getMockEWASRecords(filters?: EWASQueryFilters): AnyEWASRecord[] {
  let records: AnyEWASRecord[] = [
    ...mockACLEDEvents,
    ...mockIOMDTMRecords,
    ...mockFEWSNETRecords,
    ...mockDataminrAlerts,
    ...mockGloFASRecords,
    ...mockIDMCRecords,
  ];

  // Apply filters
  if (filters?.source) {
    const sources = Array.isArray(filters.source) ? filters.source : [filters.source];
    records = records.filter(r => sources.includes(r.source));
  }

  if (filters?.severity) {
    const severities = Array.isArray(filters.severity) ? filters.severity : [filters.severity];
    records = records.filter(r => severities.includes(r.severity));
  }

  if (filters?.region || filters?.admin1) {
    const regionFilter = filters.region || filters.admin1;
    records = records.filter(r => r.location.admin1 === regionFilter || r.location.name.includes(regionFilter!));
  }

  if (filters?.dateFrom) {
    const fromDate = new Date(filters.dateFrom);
    records = records.filter(r => new Date(r.date) >= fromDate);
  }

  if (filters?.dateTo) {
    const toDate = new Date(filters.dateTo);
    records = records.filter(r => new Date(r.date) <= toDate);
  }

  // Sort by timestamp descending (most recent first)
  records.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  return records;
}

// Source-specific data getters
export function getACLEDMockData(filters?: Omit<EWASQueryFilters, 'source'>) {
  return {
    data: getMockEWASRecords({ ...filters, source: ['acled', 'acled_cast'] }) as ACLEDEvent[],
    totalCount: mockACLEDEvents.length,
    page: 1,
    pageSize: 50,
    hasMore: false,
  };
}

export function getIOMDTMMockData(filters?: Omit<EWASQueryFilters, 'source'>) {
  return {
    data: getMockEWASRecords({ ...filters, source: ['iom_dtm', 'dtm_flash'] }) as IOMDTMRecord[],
    totalCount: mockIOMDTMRecords.length,
    page: 1,
    pageSize: 50,
    hasMore: false,
  };
}

export function getFEWSNETMockData(filters?: Omit<EWASQueryFilters, 'source'>) {
  return {
    data: getMockEWASRecords({ ...filters, source: 'fewsnet' }) as FEWSNETRecord[],
    totalCount: mockFEWSNETRecords.length,
    page: 1,
    pageSize: 50,
    hasMore: false,
  };
}

export function getDataminrMockData(filters?: Omit<EWASQueryFilters, 'source'>) {
  return {
    data: getMockEWASRecords({ ...filters, source: 'dataminr' }) as DataminrAlert[],
    totalCount: mockDataminrAlerts.length,
    page: 1,
    pageSize: 50,
    hasMore: false,
  };
}

// Export individual arrays for direct access
export {
  mockACLEDEvents,
  mockIOMDTMRecords,
  mockFEWSNETRecords,
  mockDataminrAlerts,
  mockGloFASRecords,
  mockIDMCRecords,
};
